import { useState, useEffect } from "react";
import {
  MARGIN_LEFT,
  MARGIN_TOP,
  CARD_PADDING,
  CARD_MIN_HEIGHT,
  MIN_CANVAS_SIZE,
  MAX_CANVAS_WIDTH,
  MAX_CANVAS_HEIGHT,
  LINE_FIXED_HEIGHT,
  LINE_MIN_WIDTH,
  LINE_MAX_WIDTH,
  TUNNEL_FIXED_HEIGHT,
  TUNNEL_MIN_WIDTH,
  TUNNEL_MAX_WIDTH,
} from "../utils/constants";

/**
 * @param {object} [options]
 * @param {boolean} [options.fillContainer=false] OPT-IN, live dashboard only.
 *   Two behaviours, both wrong for the editor and both deliberately off by
 *   default:
 *
 *   1. Drops the MARGIN_LEFT / MARGIN_TOP gutters. Those exist so the EDITOR
 *      can draw its axis labels (`0m` / half / full) and tick marks outside
 *      the plan. The live canvas draws neither, so on the dashboard they are
 *      54px of width and 30px of height thrown away before fitting starts.
 *      It also stops subtracting CARD_PADDING twice -- `offsetWidth` already
 *      excludes the Card's padding, so the existing formula removes 24px that
 *      is not there. (That double-subtraction is left in place for the editor:
 *      correcting it there would resize its canvas and shift every station and
 *      drag bound, and the editor is deliberately regression-only.)
 *
 *   2. For a PLANE layout, returns the whole container instead of a box
 *      fitted to the floor's aspect ratio. The canvas then fills its card and
 *      the floor is fitted INSIDE it by `createViewport` -- which already
 *      takes min(scaleX, scaleY) and puts metre (0,0) at pixel (0,0), so the
 *      plan lands top-left with the slack to its right/bottom, inside the
 *      frame rather than outside it. Line and tunnel keep their fixed lane
 *      heights: those layouts pin markers to canvas-space lanes and have no
 *      metre-space y to stretch.
 * @param {boolean} [options.fillPlane=false] The plan editor: behaviour 2 only.
 *   A plane canvas fills its card while the axis-label gutters stay, so the
 *   site sits inside a larger canvas the way it does on the live dashboard
 *   (zoom, pan and the map need room around it).
 */
export const useCanvasSize = (
  dimensions,
  open = true,
  canvasContainerRef,
  layoutType = "plane",
  { fillContainer = false, fillPlane = false } = {},
) => {
  const [canvasSize, setCanvasSize] = useState({ width: 400, height: 400 });

  useEffect(() => {
    if (!open || !dimensions.width || !dimensions.height) {
      return;
    }

    const updateCanvasSize = () => {
      const container = canvasContainerRef.current;
      if (!container) return;

      // Only the axis-label gutter comes off: `offsetWidth` is measured on a
      // child of the Card body, so the Card's padding is ALREADY excluded.
      // Subtracting CARD_PADDING here as well (as this did) removed 24px that
      // was never occupied, and left the canvas narrower than its gutter.
      const containerWidth = fillContainer
        ? container.offsetWidth
        : container.offsetWidth - MARGIN_LEFT;
      // Measure the real container instead of deriving the height from
      // CARD_MIN_HEIGHT, so a taller card (big screen) yields a bigger canvas.
      // offsetHeight already excludes the Card's padding -- only the axis-label
      // gutter comes off it. A 0 measurement (not laid out yet) falls back to
      // the old constant so the canvas can never collapse.
      const measuredHeight = container.offsetHeight;
      const gutterTop = fillContainer ? 0 : MARGIN_TOP;
      const containerHeight =
        measuredHeight > 0
          ? measuredHeight - gutterTop
          : CARD_MIN_HEIGHT - MARGIN_TOP - CARD_PADDING * 2;

      let canvasWidth, canvasHeight;

      if ((fillContainer || fillPlane) && layoutType === "plane") {
        // No aspect fit here: the canvas IS the container, and the floor is
        // fitted inside it at render time by the viewport.
        canvasWidth = Math.max(
          MIN_CANVAS_SIZE,
          Math.min(containerWidth, MAX_CANVAS_WIDTH),
        );
        canvasHeight = Math.max(
          MIN_CANVAS_SIZE,
          Math.min(containerHeight, MAX_CANVAS_HEIGHT),
        );
        setCanvasSize({ width: canvasWidth, height: canvasHeight });
        return;
      }

      if (layoutType === "line") {
        canvasHeight = LINE_FIXED_HEIGHT;

        const widthScale = Math.min(containerWidth, LINE_MAX_WIDTH) / 100;
        canvasWidth = Math.max(
          LINE_MIN_WIDTH,
          Math.min(dimensions.width * widthScale * 2, LINE_MAX_WIDTH),
        );

        if (canvasWidth > containerWidth) {
          canvasWidth = containerWidth;
        }
      } else if (layoutType === "tunnel") {
        canvasHeight = TUNNEL_FIXED_HEIGHT;

        const widthScale = Math.min(containerWidth, TUNNEL_MAX_WIDTH) / 100;
        canvasWidth = Math.max(
          TUNNEL_MIN_WIDTH,
          Math.min(dimensions.width * widthScale * 2, TUNNEL_MAX_WIDTH),
        );

        if (canvasWidth > containerWidth) {
          canvasWidth = containerWidth;
        }
      } else {
        const aspectRatio = dimensions.width / dimensions.height;

        if (containerWidth / containerHeight > aspectRatio) {
          canvasHeight = Math.min(containerHeight, MAX_CANVAS_HEIGHT);
          canvasWidth = canvasHeight * aspectRatio;
          if (canvasWidth > containerWidth) {
            canvasWidth = containerWidth;
            canvasHeight = canvasWidth / aspectRatio;
          }
        } else {
          canvasWidth = Math.min(containerWidth, MAX_CANVAS_WIDTH);
          canvasHeight = canvasWidth / aspectRatio;
          if (canvasHeight > containerHeight) {
            canvasHeight = containerHeight;
            canvasWidth = canvasHeight * aspectRatio;
          }
        }

        if (canvasWidth < MIN_CANVAS_SIZE) {
          canvasWidth = MIN_CANVAS_SIZE;
          canvasHeight = canvasWidth / aspectRatio;
          if (canvasHeight > containerHeight) {
            canvasHeight = containerHeight;
            canvasWidth = canvasHeight * aspectRatio;
          }
        }
        if (canvasHeight < MIN_CANVAS_SIZE) {
          canvasHeight = MIN_CANVAS_SIZE;
          canvasWidth = canvasHeight * aspectRatio;
          if (canvasWidth > containerWidth) {
            canvasWidth = containerWidth;
            canvasHeight = canvasWidth / aspectRatio;
          }
        }

        const maxWidth = containerWidth;
        const maxHeight = containerHeight;
        if (canvasWidth > maxWidth) {
          canvasWidth = maxWidth;
          canvasHeight = canvasWidth / aspectRatio;
        }
        if (canvasHeight > maxHeight) {
          canvasHeight = containerHeight;
          canvasWidth = canvasHeight * aspectRatio;
        }
      }

      setCanvasSize({ width: canvasWidth, height: canvasHeight });
    };

    const timeoutId = setTimeout(() => {
      updateCanvasSize();
    }, 100);

    window.addEventListener("resize", updateCanvasSize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", updateCanvasSize);
    };
  }, [dimensions, open, canvasContainerRef, layoutType, fillContainer, fillPlane]);

  return canvasSize;
};
